import { useEffect, useMemo, useState } from 'react';
import { Languages, Sparkles, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  AI_TRANSLATION_PICKS,
  getExperienceGeniusOverride,
  isExperienceEligibleForEnTranslation,
  saveExperienceTranslationEn,
  translateExperienceToEn,
  type ExperienceGeniusTranslation,
} from '../services/experienceGeniusOverrideService';

// -----------------------------------------------------------------------------
// ExperienceTranslationsPanel
// -----------------------------------------------------------------------------
// Pannello EN collassabile per tradurre i campi chiave di un'esperienza IT in
// inglese. La fonte IT viene passata dal chiamante (l'editor esperienze) via
// prop `sourceFieldsIt`. La traduzione EN viene persistita come
// `translations.en` nel documento Firestore Genius Override dell'esperienza.
// Serve sia per il CMS che per permettere al motore Dialogflow di servire i
// contenuti EN quando l'utente mobile imposta la lingua inglese.
// -----------------------------------------------------------------------------

const FIELD_DEFINITIONS: Array<{
  key: keyof ExperienceGeniusTranslation;
  label: string;
  helper?: string;
  textarea?: boolean;
  rows?: number;
}> = [
  { key: 'experienceName', label: 'Experience name (EN)' },
  { key: 'nomeChatbot', label: 'Character name (EN)' },
  { key: 'experienceDescription', label: 'Experience description (EN)', textarea: true, rows: 3 },
  { key: 'welcomeMessage', label: 'Welcome message (EN)', textarea: true, rows: 3, helper: 'Showed to the user when the experience starts.' },
  { key: 'tutorDescription', label: 'Character description (EN)', textarea: true, rows: 3 },
  { key: 'tutorPersonality', label: 'Character personality (EN)', textarea: true, rows: 2 },
  { key: 'tutorLanguageRegister', label: 'Language register (EN)' },
  { key: 'tutorEpoch', label: 'Historical epoch (EN)' },
  { key: 'fallbackMessage', label: 'Fallback message (EN)', textarea: true, rows: 2 },
  { key: 'recoveryPrompt', label: 'System prompt (EN)', textarea: true, rows: 6, helper: 'Full AI instructions used at runtime when the user selects English.' },
];

export interface ExperienceTranslationsPanelProps {
  experienceId: number | null | undefined;
  sourceFieldsIt: ExperienceGeniusTranslation;
}

export default function ExperienceTranslationsPanel({
  experienceId,
  sourceFieldsIt,
}: ExperienceTranslationsPanelProps) {
  const hasValidId = Number.isFinite(experienceId) && Number(experienceId) > 0;
  const isEligibleForTranslation = isExperienceEligibleForEnTranslation(experienceId);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<ExperienceGeniusTranslation>({});
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; msg: string } | null>(null);

  const aiSourceFields = useMemo(() => {
    const filtered: ExperienceGeniusTranslation = {};
    AI_TRANSLATION_PICKS.forEach((key) => {
      const value = String(sourceFieldsIt?.[key] || '').trim();
      if (!value) return;
      filtered[key] = value;
    });
    return filtered;
  }, [sourceFieldsIt]);

  useEffect(() => {
    if (!open || !hasValidId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const override = await getExperienceGeniusOverride(Number(experienceId));
        if (cancelled) return;
        const en = override?.translations?.en || {};
        setValues({ ...en });
      } catch (error) {
        console.warn('[ExperienceTranslationsPanel] load error', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hasValidId, experienceId]);

  const handleTranslate = async () => {
    if (!hasValidId) {
      setFeedback({ type: 'error', msg: 'Save the experience first to generate its EN translation.' });
      return;
    }
    if (!isEligibleForTranslation) {
      setFeedback({ type: 'error', msg: 'English translation is enabled only for experiences from ID 113 onwards.' });
      return;
    }
    setTranslating(true);
    setFeedback({ type: 'info', msg: 'Translating with Glooci Platform...' });
    try {
      const translated = await translateExperienceToEn({
        experienceId: Number(experienceId),
        fields: aiSourceFields,
        persist: true,
      });
      if (translated && Object.keys(translated).length > 0) {
        setValues((prev) => ({ ...translated, ...prev }));
        setFeedback({ type: 'success', msg: 'EN draft generated. You can refine and save.' });
      } else {
        setFeedback({ type: 'error', msg: 'Translation failed. Try again.' });
      }
    } catch (error) {
      console.warn('[ExperienceTranslationsPanel] translate error', error);
      setFeedback({ type: 'error', msg: 'Translation error. Retry shortly.' });
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!hasValidId) {
      setFeedback({ type: 'error', msg: 'No valid experience id.' });
      return;
    }
    if (!isEligibleForTranslation) {
      setFeedback({ type: 'error', msg: 'English translation is enabled only for experiences from ID 113 onwards.' });
      return;
    }
    setSaving(true);
    try {
      const success = await saveExperienceTranslationEn(Number(experienceId), values);
      setFeedback({
        type: success ? 'success' : 'error',
        msg: success ? 'English translation saved.' : 'Save failed.',
      });
    } catch (error) {
      console.warn('[ExperienceTranslationsPanel] save error', error);
      setFeedback({ type: 'error', msg: 'Save error.' });
    } finally {
      setSaving(false);
    }
  };

  const update = (key: keyof ExperienceGeniusTranslation, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  return (
    <div className="rounded-xl border border-border bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-primary" />
          <span className="font-semibold text-text-primary">English translation</span>
          {!hasValidId && (
            <span className="ml-2 text-xs text-text-secondary">
              (save the experience first to enable)
            </span>
          )}
          {hasValidId && !isEligibleForTranslation && (
            <span className="ml-2 text-xs text-amber-700">
              (available only from experience ID 113)
            </span>
          )}
        </div>
        <span className="text-xs text-text-secondary">
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      {open && (
        <div className="border-t border-border p-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleTranslate}
              disabled={!hasValidId || !isEligibleForTranslation || translating}
              className="btn-outline inline-flex items-center gap-2 disabled:opacity-50"
            >
              {translating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Translate with Glooci Platform
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasValidId || !isEligibleForTranslation || saving || loading}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save English translation
            </button>
            {feedback && (
              <div
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  feedback.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : feedback.type === 'error'
                      ? 'bg-red-50 text-red-700'
                      : 'bg-blue-50 text-blue-700'
                }`}
              >
                {feedback.type === 'success' ? (
                  <CheckCircle2 size={12} />
                ) : feedback.type === 'error' ? (
                  <AlertCircle size={12} />
                ) : (
                  <Loader2 size={12} className="animate-spin" />
                )}
                {feedback.msg}
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-4 text-sm text-text-secondary inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading current EN translation...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              <p className="text-xs text-text-secondary">
                Glooci Platform translates the mobile-relevant English fields, including the `Welcome message`.
                `Fallback message` and `System prompt` remain manual.
              </p>
              {FIELD_DEFINITIONS.map((field) => {
                const value = (values[field.key] as string) || '';
                const isManualOnly =
                  field.key === 'fallbackMessage'
                  || field.key === 'recoveryPrompt'
                  || field.key === 'geminiPrompt';
                return (
                  <div key={field.key} className="text-sm">
                    <label className="block text-xs font-semibold text-text-primary mb-1">
                      {field.label}
                      {isManualOnly && (
                        <span className="ml-1 font-normal text-text-secondary">(manual only)</span>
                      )}
                    </label>
                    {field.textarea ? (
                      <textarea
                        value={value}
                        rows={field.rows || 3}
                        onChange={(e) => update(field.key, e.target.value)}
                        className="w-full border border-border rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-primary/50 focus:border-primary"
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => update(field.key, e.target.value)}
                        className="w-full border border-border rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-primary/50 focus:border-primary"
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    )}
                    {field.helper && (
                      <p className="text-[11px] text-text-secondary mt-0.5">{field.helper}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
